import { posthog } from "@/lib/posthog";
import { Sentry } from "@/lib/sentry";
import { sessionAtom } from "@/state/auth.state";
import type { Session } from "@supabase/supabase-js";
import { useSetAtom } from "jotai";
import { useEffect, useRef } from "react";
import { supabase } from "./supabase";

export function AuthBridge({ children }: { children: React.ReactNode }) {
  const setSession = useSetAtom(sessionAtom);

  // Track the last identified PostHog distinct_id to avoid duplicate identify calls
  const lastIdentifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const applySession = (session: Session | null) => {
      // Update Jotai session
      setSession(session ?? null);

      const user = session?.user ?? null;

      if (!user) {
        // Logged out
        if (lastIdentifiedUserIdRef.current !== null) {
          posthog.capture("auth_logout");
        }
        lastIdentifiedUserIdRef.current = null;

        // PostHog: clears distinct_id + super props
        posthog.reset();

        // Sentry: clear user context
        Sentry.setUser(null);
        Sentry.setTag("auth_provider", null);
        Sentry.setTag("strava_connected", null);

        return;
      }

      // Logged in
      const userId = user.id;
      const provider = user.app_metadata?.provider ?? "unknown";
      const stravaAthleteId = user.user_metadata?.strava_athlete_id ?? null;

      Sentry.setUser({ id: userId });
      Sentry.setTag("auth_provider", provider);
      Sentry.setTag("strava_connected", stravaAthleteId ? "true" : "false");
      if (stravaAthleteId) {
        Sentry.setContext("strava", { athlete_id: stravaAthleteId });
      } else {
        Sentry.setContext("strava", null);
      }

      // Only run PostHog identify when user changes
      if (lastIdentifiedUserIdRef.current !== userId) {
        lastIdentifiedUserIdRef.current = userId;

        // Identify by stable internal id (best practice)
        posthog.identify(userId);

        // Set person properties
        const personProps: Record<string, string> = {};
        const name = user.user_metadata?.name;
        if (name) personProps.$name = name;
        if (user.email) personProps.email = user.email;
        if (stravaAthleteId) personProps.strava_id = String(stravaAthleteId);
        if (Object.keys(personProps).length) {
          posthog.people.set(personProps);
        }

        const utmSource = posthog.get_property("$initial_utm_source");
        const utmCampaign = posthog.get_property("$initial_utm_campaign");
        const onceProps: Record<string, string> = {};
        if (utmSource) onceProps.acquisition_source = utmSource;
        if (utmCampaign) onceProps.acquisition_campaign = utmCampaign;
        if (Object.keys(onceProps).length) {
          posthog.people.set_once(onceProps);
        }

        posthog.capture("auth_login", {
          provider,
        });
      }
    };

    supabase.auth.getSession().then(({ data, error }) => {
      if (!mounted) return;
      if (error) console.error(error);
      applySession(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [setSession]);

  return <>{children}</>;
}
